/** Modal frame. Paper panel on an ink scrim — no radius, no shadow, opened by
 *  an ink rule the way a form section is.
 *
 *  The scrim uses an explicit rgba(): opacity modifiers (/60) do not work
 *  against a bare token, so `bg-ink/60` would silently render opaque. */

import React from 'react';
import { cn } from './cn';
import { TYPE } from './primitives';

export const Dialog: React.FC<{
  title: string;
  subhead?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}> = ({ title, subhead, children, footer, wide }) => (
  <div
    className="fixed inset-0 flex items-stretch sm:items-center justify-center z-[60] sm:p-4"
    style={{ background: 'rgba(42,33,30,0.55)' }}
  >
    <div
      className={cn(
        'bg-surface border border-rule-base sm:rounded-2xl shadow-lift w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto p-5 sm:p-7',
        wide ? 'sm:max-w-[560px]' : 'sm:max-w-[440px]'
      )}
    >
      <h2 className={cn(TYPE.formTitle, 'text-ink')}>{title}</h2>
      {subhead && <div className={cn(TYPE.small, 'text-ink-soft mt-2')}>{subhead}</div>}
      <div className="mt-6">{children}</div>
      {footer && <div className="mt-6">{footer}</div>}
    </div>
  </div>
);

/** A selectable option row. Selection is the bone fill — never a colour. */
export const OptionRow: React.FC<{
  title: string;
  detail: React.ReactNode;
  warning?: string;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ title, detail, warning, busy, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full text-left p-4 border border-rule-strong hover:border-rule-base hover:bg-accent-tint transition-colors duration-[120ms] disabled:opacity-40"
  >
    <div className="flex flex-wrap items-baseline gap-x-3">
      <span className={cn(TYPE.meta, 'text-ink')}>{title}</span>
      {busy && <span className={cn(TYPE.metaTight, 'text-ink-muted')}>Saving…</span>}
    </div>
    <div className={cn(TYPE.small, 'text-ink-soft mt-1.5')}>{detail}</div>
    {warning && (
      <div className={cn(TYPE.metaTight, 'text-warn mt-2 border-l-[3px] border-warn pl-3')}>{warning}</div>
    )}
  </button>
);
